import React, { useEffect, useState } from 'react';
import {
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  SxProps,
  TextFieldProps,
  Typography,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PhoneInput from 'react-phone-number-input';

import { useLocation } from 'react-router-dom';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';
import { useDispatch } from 'react-redux';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import { Countries, TP_COLORS } from '../../../../constants';
import { createAnalytics } from '../../../../store/AnalyticsSlice';
import { saveUserDetails } from '../../../../store/AccessLimitSlice/AccessLimitApi';
import { getAuthUserFromLocalStorage } from '../../../../helper';
import { useAppSelector } from '../../../../hooks';
import { getAccessLimitState } from '../../../../store/AccessLimitSlice';
import { TPTextField } from '../../../../components/Inputs';

const stackStyles: SxProps = {
  marginTop: '30px',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: { xs: 'unset', sm: '100px', md: '150px', lg: '180px' },
};

const subTitleTypoProps: TypographyProps = {
  color: '#F1F5F9',
  textAlign: 'center',
  marginTop: '8px',
  marginBottom: '25px',
  letterSpacing: '0.05em',
  fontSize: { xs: '12px', sm: '12px', md: '16px', lg: '16px' },
  maxWidth: '340px',
};

const buttonProps: LoadingButtonProps = {
  disableElevation: true,
  variant: 'contained',
  style: {
    width: '250px',
    height: '49px',
    fontSize: '16px',
    fontWeight: '600',
    marginTop: '20px',
    backgroundColor: TP_COLORS.tutopiyaGreenMain.darken,
  },
};

const label: TypographyProps = {
  color: TP_COLORS.typo.white,
  sx: {
    mb: '2px',
    fontSize: '12px',
    ml: '10px',
  },
};

const gridStyles: SxProps = {
  marginTop: '15px',
  alignItems: 'center',
  padding: '5px',
  maxWidth: '380px',
};

const textFieldProps: TextFieldProps = {
  sx: {
    '& .MuiInputBase-root': {
      height: '48px',
      backgroundColor: 'white',
      fontSize: '14px',
    },
  },
};

// DATA
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  ? Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'Asia/Singapore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let defaultCountryCode: any = 'SG';

const defaultUserTimezone = Countries.find(
  (country) => country.value === timezone
);

if (defaultUserTimezone && defaultUserTimezone.countryCode) {
  defaultCountryCode = defaultUserTimezone.countryCode;
}

const initialState = {
  fullName: '',
  phoneNumber: '',
  country: timezone,
};

const defaultErrorData = {
  visible: false,
  message: '',
};

const LeadGenView: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  // State
  const [state, setState] = useState(initialState);
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState({
    fullName: { ...defaultErrorData },
    phoneNumber: { ...defaultErrorData },
  });
  const submitButtonDisabled =
    state.phoneNumber.trim().length < 9 || state.fullName.trim().length < 3;

  // Store
  const accessLimitState = useAppSelector(getAccessLimitState);
  const authUser = getAuthUserFromLocalStorage();
  const { isLeadGenSuccess, status } = accessLimitState;

  useEffect(() => {
    if (isLeadGenSuccess) {
      window.location.reload();
    }
  }, [isLeadGenSuccess]);

  useEffect(() => {
    if (state.fullName) {
      let _nameError = { ...defaultErrorData };

      if (state.fullName.length < 3) {
        _nameError = {
          visible: true,
          message: 'Name should have at least 3 characters',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        fullName: _nameError,
      }));
    }
  }, [state.fullName]);

  useEffect(() => {
    if (state.phoneNumber) {
      let _nameError = { ...defaultErrorData };

      if (state.phoneNumber.length < 9) {
        _nameError = {
          visible: true,
          message: 'Phone Number should have at least 9 numbers',
        };
      }

      setErrors((prevState) => ({
        ...prevState,
        phoneNumber: _nameError,
      }));
    }
  }, [state.phoneNumber]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.currentTarget;
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleButtonClick = () => {
    if (submitButtonDisabled) {
      setFormError('Please fill out the details');
    } else if (!submitButtonDisabled && authUser) {
      setFormError('');
      dispatch(
        createAnalytics({
          type: 'LP - Leadgen Form',
          data: {
            fullName: state.fullName,
            phoneNumber: state.phoneNumber,
            country: state.country,
            source: 'Content',
            url: location.pathname.replace('/learning-portal/dashboard/', ''),
          },
          userId: authUser?._id,
        })
      );
      dispatch(
        saveUserDetails({
          fullName: state.fullName,
          phoneNumber: state.phoneNumber,
          country: state.country,
          userId: authUser._id,
        })
      );
    }
  };

  return (
    <Stack sx={{ ...stackStyles }}>
      <img
        src={`${process.env.PUBLIC_URL}/images/leadGenModal/trophy.png`}
        width={belowMd ? '120px' : '215px'}
        height={belowMd ? '120px' : '215px'}
        alt="tropy"
      />
      <Typography
        fontSize={belowMd ? '20px' : '30px'}
        fontWeight="bold"
        color={TP_COLORS.typo.white}
      >
        Unlock Your Personalized Report
      </Typography>
      <Typography {...subTitleTypoProps}>
        Fill in your details so we can generate your personalised performance
        report
      </Typography>
      <Grid container sx={gridStyles}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <Typography {...label}>Full Name</Typography>
            <TPTextField
              {...textFieldProps}
              name="fullName"
              type="text"
              onChange={handleInputChange}
              placeholder="Type your full name"
              error={errors.fullName.visible}
              helperText={errors.fullName.message || ' '}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Typography {...label}>Phone Number</Typography>
          <PhoneInput
            international
            defaultCountry={defaultCountryCode}
            placeholder="Enter phone number"
            name="phoneNumber"
            value={state.phoneNumber}
            onChange={(value) => {
              if (value) {
                setState((prevState) => ({
                  ...prevState,
                  phoneNumber: value,
                }));
              }
            }}
            className="phone-number-input-custom-styles"
            style={{
              height: '48px',
              backgroundColor: '#F1F5F9',
              borderColor: '#647488',
            }}
            numberInputProps={{
              style: {
                height: '48px',
                borderColor: errors.phoneNumber.visible ? '#FF2B2D' : '#CBD5E1',
                fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
                fontSize: '14px',
              },
            }}
          />
          <FormHelperText style={{ marginLeft: '14px', color: '#FF2B2D' }}>
            {errors.phoneNumber.message || ' '}
          </FormHelperText>
        </Grid>
      </Grid>
      <LoadingButton
        {...buttonProps}
        onClick={handleButtonClick}
        loading={status === 'loading'}
        startIcon={<LockOpenRoundedIcon />}
      >
        Unlock My Full Report
      </LoadingButton>
      {formError && submitButtonDisabled && (
        <Typography
          color="error"
          fontSize="13px"
          style={{ marginTop: '10px', textAlign: 'center' }}
        >
          {formError}
        </Typography>
      )}
    </Stack>
  );
};

export default LeadGenView;
